import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Skeleton } from '@mui/material'
import SummaryGrid from './SummaryGrid'
import StudentSummaryHeader from './StudentSummaryHeader'
import { getStudentSummary } from '../../../../services/dashboard/student-summary.service'

// MF-526 — Student summary container. Self-gated (only renders for a Student when the tenant
// feature flag is on), so dropping it into the home is a one-liner and it stays inert everywhere
// else. Composes the identity header + the metric card grid; presentation (label / tone / chart
// type / axis) lives here, the data comes from analytics.
//
// Response shape: { student, metrics }. `student` feeds the header; `metrics` is per-metric
// { current, prev, series: [{ t, v }] } (attendance / lates / points / gpa). A metric absent from
// the payload simply doesn't render — never a broken card.
const METRICS = [
  {
    key: 'attendance',
    label: 'School Attendance',
    unit: '%',
    tone: 'good',
    chart: 'line',
    yMax: 100,
    higherIsBetter: true,
    period: 'This Term',
    deltaText: 'than past Term',
    deltaUnit: ' pp',
    gpa: false
  },
  {
    key: 'lates',
    label: 'Lates to Class',
    unit: '',
    tone: 'bad',
    chart: 'bar',
    higherIsBetter: false,
    period: 'This Term',
    deltaText: 'than past Term',
    deltaUnit: '',
    gpa: false
  },
  {
    key: 'gpa',
    label: 'GPA',
    unit: '',
    tone: 'good',
    chart: 'line',
    yMax: 4,
    higherIsBetter: true,
    period: 'This Report',
    deltaText: 'than past Report',
    deltaUnit: '',
    gpa: true
  },
  {
    key: 'points',
    label: 'Positive Behaviour Points',
    unit: '',
    tone: 'good',
    chart: 'bar',
    higherIsBetter: true,
    period: 'This Term',
    deltaText: 'than past Term',
    deltaUnit: '',
    gpa: false
  }
]

const fmtValue = (v, isGpa) => (isGpa ? Number(v).toFixed(2) : Math.round(Number(v)))

const buildStats = (data) =>
  METRICS.map((m) => {
    const d = data?.[m.key]
    if (!d || d.current == null) return null
    let delta = null
    if (d.prev != null) {
      const diff = Number(d.current) - Number(d.prev)
      // Compare on the DISPLAYED magnitude so a change that rounds to 0 reads as "no change"
      // (a coloured ▲0/▼0 would wrongly imply an improvement/decline).
      const value = m.gpa ? Math.abs(diff).toFixed(2) : Math.round(Math.abs(diff))
      const flat = Number(value) === 0
      delta = flat
        ? { flat: true }
        : {
            value,
            unit: m.deltaUnit,
            dir: diff > 0 ? 'up' : 'down',
            // Tone is by GOODNESS, not direction: fewer lates is good, fewer points is not.
            good: diff > 0 === m.higherIsBetter,
            text: m.deltaText
          }
    }
    return {
      key: m.key,
      label: m.label,
      value: fmtValue(d.current, m.gpa),
      unit: m.unit,
      period: m.period,
      tone: m.tone,
      delta,
      chart: m.chart,
      yMax: m.yMax,
      trend: d.series || []
    }
  }).filter(Boolean)

const SkeletonGrid = () => (
  <Box
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }
    }}
  >
    {[0, 1, 2, 3].map((i) => (
      <Skeleton key={i} variant='rounded' height={440} sx={{ borderRadius: '14px' }} />
    ))}
  </Box>
)

const StudentSummary = () => {
  // MF-542 — the summary is now surfaced as a bi-custom Analytics chart (assigned per role via
  // Analytics Charts), not a home widget behind the `showStudentSummary` flag. It just renders the
  // logged-in student's own summary; non-students get nothing.
  const isStudent = useSelector((s) => s?.session?.profile?.roles?.some((r) => ['Student'].includes(r.id)))
  const [state, setState] = useState({ loading: true, data: null })

  useEffect(() => {
    if (!isStudent) return undefined
    let alive = true
    getStudentSummary()
      .then((d) => alive && setState({ loading: false, data: d }))
      .catch(() => alive && setState({ loading: false, data: null }))
    return () => {
      alive = false
    }
  }, [isStudent])

  if (!isStudent) return null
  if (state.loading) return <SkeletonGrid />

  // Response shape: { student, metrics }. Header comes from `student`, cards from `metrics`.
  const student = state.data?.student || null
  const stats = buildStats(state.data?.metrics)
  if (!student && !stats.length) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <StudentSummaryHeader student={student} />
      {stats.length ? <SummaryGrid stats={stats} /> : null}
    </Box>
  )
}

export default StudentSummary
